import { Link } from 'react-router';
import { Scene3D } from '@/components/motion/Scene3D';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/features/auth/use-auth';
import { useAuthDialog } from '@/features/auth/use-auth-dialog';
import { useGeeCoinAccount } from '../api';
import { coins } from '../format';
import '../market.css';

export function CoinMark({ small = false }: { small?: boolean }) {
  return (
    <span className={`gc-mark${small ? ' gc-mark-small' : ''}`} aria-hidden="true">
      G
    </span>
  );
}

export function MarketHero({ coinPage = false }: { coinPage?: boolean }) {
  return (
    <header className="market-hero">
      <div className="market-hero-copy">
        <p className="market-eyebrow">
          <span /> GEEFAMILY COMMUNITY
        </p>
        <h1>
          {coinPage ? (
            <>
              Topluluğun enerjisi.
              <br />
              <em>GeeCoin.</em>
            </>
          ) : (
            <>
              Topluluğun bir parçası ol.
              <br />
              <em>Kazandıklarını keşfet.</em>
            </>
          )}
        </h1>
        <p className="market-hero-description">
          {coinPage
            ? 'Etkinliklerle biriktir, Market’te dilediğin ürüne dönüştür. Kazandığın puanları ve seçimlerini tek yerde takip et.'
            : 'Etkinliklerden kazandığın GeeCoin’ler, GeeFamily Market’te seçtiğin ürünlerle buluşuyor.'}
        </p>
        <div className="market-hero-links">
          <Link to={coinPage ? '/market' : '/geecoin'} className="gf-button gf-button-primary">
            {coinPage ? 'Market’i keşfet' : 'GeeCoin’i tanı'} <span aria-hidden>↗</span>
          </Link>
          <a href={coinPage ? '#geecoin-history' : '#market-catalog'} className="market-text-link">
            {coinPage ? 'Hesabımı görüntüle' : 'Ürünlere göz at'} <span aria-hidden>↓</span>
          </a>
        </div>
      </div>
      <div className="market-hero-art">
        <Scene3D kind="coin" />
        <span className="market-art-caption">EARN · COLLECT · REDEEM</span>
      </div>
    </header>
  );
}

export function AccountStrip({ detailed = false }: { detailed?: boolean }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { open } = useAuthDialog();
  const account = useGeeCoinAccount();
  return (
    <section
      className={`gc-account${detailed ? ' gc-account-detailed' : ''}`}
      aria-label="GeeCoin hesabım"
    >
      <div className="gc-account-identity">
        <CoinMark />
        <div>
          <p className="market-eyebrow">GEECOIN HESABIN</p>
          <p className="text-sm text-surface-400">Topluluk puanların, tek yerde.</p>
        </div>
      </div>
      {isLoading ? (
        <p role="status">Oturum kontrol ediliyor…</p>
      ) : !isAuthenticated ? (
        <div className="gc-account-signin">
          <p>Puanlarını görmek için hesabına giriş yap.</p>
          <Button onClick={() => open('login')}>Giriş yap</Button>
        </div>
      ) : account.isPending ? (
        <p role="status">GeeCoin bakiyen yükleniyor…</p>
      ) : account.isError ? (
        <LoadError
          label="GeeCoin bakiyen yüklenemedi."
          retry={() => {
            void account.refetch();
          }}
        />
      ) : (
        <>
          <div className="gc-account-stats">
            <div>
              <p>Kullanılabilir</p>
              <strong>
                {coins(account.data.balance)} <small>GC</small>
              </strong>
            </div>
            {detailed ? (
              <>
                <div>
                  <p>Toplam kazanılan</p>
                  <strong>
                    {coins(account.data.totalEarned)} <small>GC</small>
                  </strong>
                </div>
                <div>
                  <p>Net harcanan</p>
                  <strong>
                    {coins(account.data.totalSpent)} <small>GC</small>
                  </strong>
                </div>
              </>
            ) : null}
          </div>
          {!detailed ? (
            <Link to="/geecoin" className="market-text-link">
              Hesap hareketleri ↗
            </Link>
          ) : null}
        </>
      )}
    </section>
  );
}

export function LoadError({
  label = 'Veriler yüklenemedi.',
  retry,
}: {
  label?: string;
  retry: () => void;
}) {
  return (
    <div className="market-load-error" role="alert">
      <p>{label}</p>
      <Button variant="secondary" onClick={retry}>
        Tekrar dene
      </Button>
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  totalCount,
  onPage,
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  onPage: (page: number) => void;
}) {
  if (totalCount <= pageSize) return null;
  const totalPages = Math.ceil(totalCount / pageSize);
  return (
    <nav className="market-pagination" aria-label="Sayfalama">
      <Button variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Önceki
      </Button>
      <span>
        Sayfa {page} / {totalPages}
      </span>
      <Button variant="secondary" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
        Sonraki
      </Button>
    </nav>
  );
}
